import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check } from "lucide-react";

const AreasPage = () => {
  const walesAreas = [
    { name: "Cardiff", postcodes: "CF1-CF99", link: "/areas/cardiff" },
    { name: "Newport", postcodes: "NP1-NP99", link: "/areas/newport" },
    { name: "Swansea", postcodes: "SA1-SA99", link: "/areas/swansea" },
    { name: "Barry", postcodes: "CF62-CF63" },
    { name: "Pontypridd", postcodes: "CF37-CF38" },
    { name: "Caerphilly", postcodes: "CF83" }
  ];

  const englandAreas = [
    { name: "London", postcodes: "All postcodes" },
    { name: "Bristol", postcodes: "BS1-BS99" },
    { name: "Birmingham", postcodes: "B1-B99" },
    { name: "Reading", postcodes: "RG1-RG99" },
    { name: "Oxford", postcodes: "OX1-OX99" },
    { name: "Bath", postcodes: "BA1-BA99" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Areas We Cover | Removals Across Wales & England | Hitech Transit"
        description="Hitech Transit covers Cardiff, Newport, Swansea, Bristol, London and beyond. Local and long-distance removals across Wales and England. Get a free quote."
        canonical="/areas-we-cover"
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={[{ label: "Areas We Cover", href: "/areas-we-cover" }]} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Coverage
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Areas We Cover
              </h1>
              <p className="text-slate-300 text-lg">
                With teams based in Cardiff and London, we provide removal services across 
                Wales and England. Local, regional, or national - we've got you covered.
              </p>
            </div>
          </div>
        </section>

        {/* Wales */}
        <section className="section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A]">Wales</h2>
                </div>
                <p className="text-slate-600 mb-8">
                  Our Cardiff headquarters serves all of South Wales and beyond. Whether you're 
                  moving within the city or across the country.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {walesAreas.map((area, index) => (
                    area.link ? (
                      <Link key={area.name || `area-${index}`} to={area.link} className="card-surface p-4 hover:border-[#FF5A1F] transition-colors group">
                        <div className="font-semibold text-[#0F172A] group-hover:text-[#FF5A1F] transition-colors flex items-center gap-1">
                          {area.name} <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="text-sm text-slate-500">{area.postcodes}</div>
                      </Link>
                    ) : (
                      <div key={area.name || `area-${index}`} className="card-surface p-4">
                        <div className="font-semibold text-[#0F172A]">{area.name}</div>
                        <div className="text-sm text-slate-500">{area.postcodes}</div>
                      </div>
                    )
                  ))}
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A]">England</h2>
                </div>
                <p className="text-slate-600 mb-8">
                  Our London team covers Greater London and surrounding areas, with regular 
                  routes to Wales and the Midlands.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {englandAreas.map((area, index) => (
                    <div key={area.name || `area-${index}`} className="card-surface p-4">
                      <div className="font-semibold text-[#0F172A]">{area.name}</div>
                      <div className="text-sm text-slate-500">{area.postcodes}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Long Distance */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                Long Distance Moves
              </h2>
              <p className="text-slate-600 mb-8">
                Moving between Wales and England? We run regular services on the Cardiff-London 
                corridor and can arrange moves to anywhere in the UK.
              </p>
              <div className="space-y-3 text-left max-w-md mx-auto mb-8">
                {[
                  "Cardiff to London from £350",
                  "London to Cardiff from £350",
                  "Cardiff to Bristol from £200",
                  "All UK destinations available"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="areas-quote-btn">
                Get Your Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AreasPage;
